import cornerstone from 'cornerstone-core';
import OHIF from '@ohif/core';

/**
 * Naturalized PN can be a plain string or a dcmjs `{ Alphabetic }` object.
 */
const _getPN = value => {
  if (!value) {
    return;
  }
  if (Array.isArray(value)) {
    return _getPN(value[0]);
  }
  return typeof value === 'string' ? value : value.Alphabetic;
};

/**
 * Patient/study level fields of an image.
 *
 * They are read from the study JSON returned by the API (stored in the OHIF
 * metadata provider by StandaloneRouting) because the patient can be edited in
 * the API after the images were acquired, so the DICOM headers of the images
 * may be out of date. The image headers are only used when the JSON lacks a
 * value. The OHIF provider is not registered in cornerstone (see init.js), so
 * `cornerstone.metaData.get` alone would only return the image headers.
 *
 * @param {string} imageId
 * @returns {Object} { patientName, patientId, studyDate, studyTime, studyDescription, institution }
 */
export default function getPatientStudyInfo(imageId) {
  if (!imageId) {
    return {};
  }

  const json =
    OHIF.cornerstone.metadataProvider.getInstance(imageId) ||
    // Multiframe imageIds carry a frame parameter that the JSON urls do not.
    OHIF.cornerstone.metadataProvider.getInstance(
      imageId.replace(/[?&]frame=\d+/i, '')
    ) ||
    {};
  const patient = cornerstone.metaData.get('patientModule', imageId) || {};
  const study = cornerstone.metaData.get('generalStudyModule', imageId) || {};
  // InstitutionName is not part of any of the wado-image-loader modules.
  const instance = cornerstone.metaData.get('instance', imageId) || {};

  return {
    patientName: _getPN(json.PatientName) || patient.patientName,
    patientId: json.PatientID || patient.patientId,
    studyDate: json.StudyDate || study.studyDate,
    studyTime: json.StudyTime || study.studyTime,
    studyDescription: json.StudyDescription || study.studyDescription,
    institution: json.InstitutionName || instance.InstitutionName,
  };
}
